import { selectors as authSelectors } from '@hixme/auth-ui'
import { useStableUsRestrictedDataAccess } from 'enrollment-platform/rbac/useStableUsRestrictedDataAccess'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { RoleVO } from 'helpers/domain'
import { useSelector } from 'react-redux'

import {
  OpenEnrollmentAccessVO,
  RenderableOpenEnrollmentSection,
} from '../../domain'
import { useCustomerConfiguration } from './use-customer-configuration'

type UseOpenEnrollmentAccessReturn = {
  hasAccess: boolean
  isAccessLoading: boolean
  // EMS-10967: also waits for the US-restricted check — use it for anything
  // that reads accessibleSections (tabs, redirects, skeleton). The sidebar
  // keeps isAccessLoading, since hasAccess doesn't depend on that check.
  isSectionsLoading: boolean
  isAccessError: boolean
  accessibleSections: readonly RenderableOpenEnrollmentSection[]
}

export const useOpenEnrollmentAccess = (): UseOpenEnrollmentAccessReturn => {
  const role = useSelector(authSelectors.getRole)
  const { flags } = useFeatureFlags()
  const roleVO = role ? RoleVO.fromString(role as string) : null
  // EMS-10967: the People tab needs UsRestrictedData:Manage.
  const { hasUsRestrictedDataAccess, isLoading: isUsRestrictedAccessLoading } =
    useStableUsRestrictedDataAccess()
  const { customerConfigs, isLoading, isError } = useCustomerConfiguration({
    enabled: roleVO?.isClientAdmin() ?? false,
  })

  if (!roleVO) {
    return {
      hasAccess: false,
      isAccessLoading: true,
      isSectionsLoading: true,
      isAccessError: false,
      accessibleSections: [],
    }
  }

  const access = OpenEnrollmentAccessVO.create({
    role: roleVO,
    featureFlags: {
      isOpenEnrollmentFeatureEnabled: Boolean(
        flags['admin.abp-5.open-enrollment']
      ),
      isApprovalTabEnabled: Boolean(flags['admin.abp-5.show-approval-tab']),
    },
    customerConfigs,
    // No access while the check loads, so People is never prefetched early.
    canViewUsRestrictedData: hasUsRestrictedDataAccess,
  })

  const isPlatformAdmin = access.role.isPlatformHixmeAdmin()
  const isAccessLoading = isPlatformAdmin ? false : isLoading

  return {
    hasAccess: access.hasAccess(),
    isAccessLoading,
    isSectionsLoading: isAccessLoading || isUsRestrictedAccessLoading,
    isAccessError: isPlatformAdmin ? false : isError,
    accessibleSections: access.listAccessibleSections(),
  }
}
